import Dexie, { type EntityTable, type Transaction } from 'dexie';
import type { SiteLogo, SocialLink, TopNavData } from '@/navTree';
import {
  ensureStaleContentCacheCleared,
  getContentDbName,
  getExpectedCacheGeneration,
} from './contentCache';

await ensureStaleContentCacheCleared();

/** Page item shape from Wagtail pages API (see WAGTAIL_API.md). */
export interface PageItemMeta {
  type: string;
  detail_url: string;
  /** Front-end URL; may be null when API is not configured to serve pages. */
  html_url?: string | null;
  /** URL path (e.g. /home/about/). Use to build links when html_url is null. */
  url_path: string;
  /** Materialized path for tree location (e.g. 000100020003). */
  path: string;
  slug: string;
  first_published_at: string | null;
  last_published_at: string | null;
  /** Parent page id; null for root. */
  parent?: number | null;
  /** Page id this is an alias of; null if not an alias. */
  alias_of?: number | null;
  locale: string;
  translation_key?: string;
  /** SEO title; used for document title when set. */
  seo_title?: string | null;
  /** Meta description for search and social. */
  search_description?: string | null;
  /** Shared page icon (from BaseContentPage), if present. */
  icon?: string | null;
  /** Wagtail menu visibility flag. */
  show_in_menus?: boolean;
}

/** News article category (from listing/detail API). */
export interface NewsCategoryRef {
  id: number;
  slug: string;
  name: string;
}

/** News article tag (from listing/detail API). */
export interface NewsTagRef {
  slug: string;
  name: string;
}

/** Sibling locale row from pages listing (canonical tree + locale_variants). */
export interface PageLocaleVariantListing {
  id: number;
  title: string;
  url_path: string;
  path: string;
  slug: string;
  last_published_at: string | null;
}

export interface PageItem {
  id: number;
  meta: PageItemMeta;
  title: string;
  /**
   * Other locales for the same translation_key (listing only). Tetum row is the item itself.
   */
  locale_variants?: Partial<Record<string, PageLocaleVariantListing>>;
  /** Rich HTML title (primary_text only); when set, prefer for visible headings. */
  rich_title?: string | null;
  /** Present on news article listing items. */
  date?: string | null;
  category?: NewsCategoryRef | null;
  tags?: NewsTagRef[] | null;
  /** Promote image payload from listing/detail API (news cards, etc.). */
  image?: unknown;
  /**
   * Listing/detail: NormalPage minimal | content; NewsIndex synthetic minimal;
   * HomePage and news articles null (layout from meta.type).
   */
  header_style?: 'minimal' | 'content' | null;
}

/**
 * One row per site: ETag and last canonical (default-locale) listing snapshot for offline bootstrap.
 */
export interface SiteRow {
  id: string;
  etag: string | null;
  navigation?: Record<string, TopNavData>;
  /** Last successful GET /api/v2/pages/ `items` (canonical locale only). */
  listingItems?: PageItem[];
  /** Set when listing response is merged under the current client cache generation. */
  siteClientGeneration?: string;
}

/**
 * IndexedDB `page` table: **one row per `translation_key`** (logical page), not per Wagtail id.
 * `listingItem` is the canonical-locale listing row (includes `locale_variants`).
 * `locales` holds full CMS detail JSON per locale after a bundle fetch.
 * After a bundle/detail fetch, **detailClientGeneration** matches `VITE_CACHE_GENERATION` for soft invalidation.
 */
export interface PageRow {
  translation_key: string;
  /** Canonical (default locale) Wagtail page id. */
  canonical_page_id: number;
  /** All physical page ids (canonical + translations) for lookup. */
  all_page_ids: number[];
  listingItem: PageItem;
  /** Published detail body per locale (same shape as former GET /pages/id/). */
  locales?: Partial<Record<string, Record<string, unknown>>>;
  /** Per-locale meta.last_published_at when detail was stored. */
  detailDates?: Partial<Record<string, string | null>>;
  /** Last detail bundle fetch under the current client cache generation contract. */
  detailClientGeneration?: string;
}

/**
 * Single-locale page payload for the Vue app (route + components). Derived from PageRow + locale.
 */
export interface PageDetailRow {
  id: number;
  title: string;
  meta: PageItemMeta;
  /** Set from detail API meta.last_published_at when we store the detail response. */
  detailDate?: string | null;
  /** Matches getExpectedCacheGeneration() when this row’s detail payload was stored from the API. */
  detailClientGeneration?: string;
  /** Site name (HomePage promote/footer); used for nav branding. */
  site_name?: string;
  site_subtitle?: string;
  site_logos?: SiteLogo[];
  footer_logo?: SiteLogo | null;
  footer_logo_text?: string;
  social_links?: SocialLink[];
  top_menu?: {
    pages: {
      id?: number;
      page_id?: number;
      title?: string;
      url?: string;
      translation_key?: string;
      icon?: string;
    }[];
  };
  copyright_text?: string;
  date?: string | null;
  category?: NewsCategoryRef | null;
  tags?: NewsTagRef[] | null;
  rich_title?: string | null;
  header_style?: 'minimal' | 'content' | null;
  read_more_text?: string | null;
  read_more_anchor?: string | null;
  page_links?: { pages: { page_id: number }[] } | null;
  partners?: SiteLogo[];
  /** HomePage only: account / login page heading (SPA `/account`). */
  login_title?: string | null;
  /** HomePage only: account / login page supporting text. */
  login_message?: string | null;
  /** HomePage only: account / login page image (same shape as hero `image`). */
  login_image?: unknown;
  /** TrainingCentreDetailPage: linked snippet id. */
  training_centre_id?: number | null;
  program_id?: number | null;
  /** Training / program detail: rich-text-only stream for the About card. */
  about_content?: unknown;
  filter_card_title?: string | null;
  [key: string]: unknown;
}

/** True if the slice's detail payload matches listing freshness for that locale. */
export function isDetailUpToDate(row: PageDetailRow): boolean {
  const listingAt = row.meta?.last_published_at;
  const detailAt = row.detailDate;
  return detailAt != null && listingAt != null && detailAt === listingAt;
}

/** True if this slice was stored under the current VITE_CACHE_GENERATION. */
export function isDetailFreshForCurrentBuild(row: PageDetailRow): boolean {
  return row.detailClientGeneration === getExpectedCacheGeneration();
}

/** CMS-fresh and client-contract-fresh (no refetch needed for normal operation). */
export function isDetailRowFullyFresh(row: PageDetailRow): boolean {
  return isDetailUpToDate(row) && isDetailFreshForCurrentBuild(row);
}

/**
 * IndexedDB object store for logical pages (one row per `translation_key`).
 * Dexie cannot change an existing store’s primary key; legacy `page` used `id`, so this
 * store was added in v3 and the old `page` store is removed in v4. `ContentDb.page` maps here.
 */
export const CONTENT_DB_PAGE_STORE = 'pageByTranslationKey' as const;

const LEGACY_PAGE_DEFAULT_LOCALE = 'tet';

type LegacyPageRow = {
  id?: number;
  title?: string;
  meta?: { translation_key?: string; locale?: string } & Record<string, unknown>;
  detailDate?: string | null;
  [k: string]: unknown;
};

/** Copy legacy id-keyed `page` rows into `pageByTranslationKey` (v3 upgrade). */
async function migrateLegacyPageStoreToTranslationKey(
  tx: Transaction,
  legacyTable: Dexie.Table,
  targetTable: Dexie.Table
): Promise<void> {
  const oldRows = (await legacyTable.toArray()) as LegacyPageRow[];
  await legacyTable.clear();
  const byTk = new Map<string, LegacyPageRow[]>();
  for (const p of oldRows) {
    const tk = p.meta?.translation_key;
    if (typeof tk !== 'string' || !tk) continue;
    if (!byTk.has(tk)) byTk.set(tk, []);
    byTk.get(tk)!.push(p);
  }
  for (const [tk, rows] of byTk) {
    const canonical =
      rows.find((r) => r.meta?.locale === LEGACY_PAGE_DEFAULT_LOCALE) ?? rows[0];
    if (!canonical?.id || !canonical.meta) continue;
    const locale_variants: PageItem['locale_variants'] = {};
    for (const r of rows) {
      if (!r.meta) continue;
      const loc = r.meta.locale;
      if (!loc || loc === LEGACY_PAGE_DEFAULT_LOCALE || r.id === canonical.id) continue;
      if (!r.meta.url_path || !r.meta.path) continue;
      const m = r.meta as { slug?: string };
      locale_variants[loc] = {
        id: r.id!,
        title: typeof r.title === 'string' ? r.title : '',
        url_path: String(r.meta.url_path),
        path: String(r.meta.path),
        slug: String(m.slug ?? ''),
        last_published_at:
          typeof r.meta.last_published_at === 'string' ? r.meta.last_published_at : null,
      };
    }
    const listingItem: PageItem = {
      id: canonical.id,
      title: typeof canonical.title === 'string' ? canonical.title : '',
      meta: (canonical.meta ?? {}) as unknown as PageItem['meta'],
      locale_variants:
        Object.keys(locale_variants).length > 0 ? locale_variants : undefined,
    };
    const locales: NonNullable<PageRow['locales']> = {};
    const detailDates: NonNullable<PageRow['detailDates']> = {};
    for (const r of rows) {
      if (!r.meta || !r.id) continue;
      const loc = r.meta.locale;
      if (!loc) continue;
      locales[loc] = { ...r, id: r.id } as Record<string, unknown>;
      detailDates[loc] =
        typeof r.detailDate === 'string'
          ? r.detailDate
          : typeof r.meta.last_published_at === 'string'
            ? r.meta.last_published_at
            : null;
    }
    const allIds = rows.map((r) => r.id!).filter((id) => typeof id === 'number');
    await targetTable.put({
      translation_key: tk,
      canonical_page_id: canonical.id,
      all_page_ids: [...new Set(allIds)],
      listingItem,
      locales,
      detailDates,
    });
  }
}

export class ContentDb extends Dexie {
  site!: EntityTable<SiteRow, 'id'>;
  page!: EntityTable<PageRow, 'translation_key'>;

  constructor() {
    super(getContentDbName());
    this.version(1).stores({
      site: 'id',
      page: 'id',
    });
    this.version(2).stores({
      site: 'id',
      page: 'id',
    });
    // Keep legacy `page` (PK id); add a new store for translation_key PK (Dexie cannot alter PK in place).
    this.version(3)
      .stores({
        site: 'id',
        page: 'id',
        [CONTENT_DB_PAGE_STORE]: 'translation_key, canonical_page_id',
      })
      .upgrade(async (tx) => {
        await migrateLegacyPageStoreToTranslationKey(
          tx,
          tx.table('page'),
          tx.table(CONTENT_DB_PAGE_STORE)
        );
      });
    // Drop legacy `page` store; app uses `this.page` → physical `pageByTranslationKey`.
    this.version(4).stores({
      site: 'id',
      [CONTENT_DB_PAGE_STORE]: 'translation_key, canonical_page_id',
    });

    this.page = this.table(CONTENT_DB_PAGE_STORE);
  }
}

export const contentDb = new ContentDb();
